import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import publicApi from "@/lib/publicApi";
import { readError } from "@/lib/api";
import { formatEurPlain } from "@/lib/money";
import HCaptchaWidget from "@/components/HCaptchaWidget";

/**
 * Custom-build enquiry modal. Renders a read-only summary of the configuration
 * + a contact form. On submit → POST /api/inquiries.
 */
export default function InquiryDialog({
  open,
  onOpenChange,
  product,         // { id, name, base_price_eur }
  configuration,   // [{ field_id, field_label, value, value_label, price_delta_eur }]
  totalPriceEur,
  locale = "hr",
  labels,          // { title, subtitle, name, email, phone, prefer, prefer_email, prefer_phone, message, submit, sending, success_title, success_body, error, summary, total }
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_contact: "email", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const captchaRef = useRef(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setBusy(true);
    try {
      const hcaptcha_token = captchaRef.current?.getToken() || "";
      if (captchaRef.current?.isConfigured() && !hcaptcha_token) {
        setError(labels.captcha_required || "Please complete the captcha.");
        setBusy(false);
        return;
      }
      await publicApi.post("/inquiries", {
        product_slug: product.id,
        configuration,
        base_price_eur: product.base_price_eur,
        total_price_eur: totalPriceEur,
        customer: { ...form, email: form.email.trim(), name: form.name.trim() },
        message: form.message,
        locale,
        hcaptcha_token,
        website: form.website,
      });
      setDone(true);
    } catch (err) {
      setError(readError(err, labels.error));
      captchaRef.current?.reset?.();
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    if (done) {
      setTimeout(() => {
        setDone(false);
        setForm({ name: "", email: "", phone: "", preferred_contact: "email", message: "" });
      }, 250);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="bg-card border-border max-w-lg" data-testid="inquiry-dialog">
        {done ? (
          <div className="text-center py-6" data-testid="inquiry-success">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" strokeWidth={1.2} />
            <DialogTitle className="font-serif text-2xl">{labels.success_title}</DialogTitle>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">{labels.success_body}</p>
            <Button className="mt-6 shadow-interactive" onClick={close} data-testid="inquiry-close">{labels.close || "Close"}</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">{labels.title}</DialogTitle>
              <DialogDescription>{labels.subtitle}</DialogDescription>
            </DialogHeader>

            <div className="rounded-lg bg-muted/40 border border-border/60 p-4 max-h-44 overflow-y-auto" data-testid="inquiry-summary">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{labels.summary}</div>
              <ul className="space-y-1 text-sm">
                {configuration.filter((c) => c.value_label).map((c) => (
                  <li key={c.field_id} className="flex items-baseline justify-between gap-3">
                    <span className="text-foreground/80">{c.field_label}: <span className="text-foreground">{c.value_label}</span></span>
                    {c.price_delta_eur > 0 && (
                      <span className="text-xs text-muted-foreground whitespace-nowrap">+{formatEurPlain(c.price_delta_eur)}</span>
                    )}
                  </li>
                ))}
              </ul>
              <div className="mt-3 pt-3 border-t border-border/60 flex items-baseline justify-between">
                <span className="font-medium text-foreground">{labels.total}</span>
                <span className="font-serif text-xl text-primary">{formatEurPlain(totalPriceEur)}</span>
              </div>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">{labels.name}</Label>
                  <Input required value={form.name} disabled={busy} onChange={onChange("name")} className="h-10 bg-background" data-testid="inquiry-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{labels.email}</Label>
                  <Input required type="email" value={form.email} disabled={busy} onChange={onChange("email")} className="h-10 bg-background" data-testid="inquiry-email" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{labels.phone}</Label>
                <Input value={form.phone} disabled={busy} onChange={onChange("phone")} className="h-10 bg-background" data-testid="inquiry-phone" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{labels.prefer}</Label>
                <RadioGroup
                  value={form.preferred_contact}
                  onValueChange={(v) => setForm((f) => ({ ...f, preferred_contact: v }))}
                  className="flex gap-4"
                >
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <RadioGroupItem value="email" data-testid="inquiry-prefer-email" />
                    {labels.prefer_email}
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <RadioGroupItem value="phone" data-testid="inquiry-prefer-phone" />
                    {labels.prefer_phone}
                  </label>
                </RadioGroup>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{labels.message}</Label>
                <Textarea value={form.message} disabled={busy} onChange={onChange("message")} rows={3} className="bg-background resize-none" data-testid="inquiry-message" />
              </div>
              <HCaptchaWidget ref={captchaRef} />
              {/* Honeypot — invisible to humans, irresistible to bots. */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.website}
                onChange={onChange("website")}
                className="absolute opacity-0 -left-[9999px] h-0 w-0 pointer-events-none"
              />
              {error && (
                <div className="text-sm rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive flex items-start gap-2" data-testid="inquiry-error">
                  <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} /> {error}
                </div>
              )}
              <DialogFooter className="flex-row gap-2 sm:gap-2">
                <Button type="button" variant="outline" onClick={close} disabled={busy}>{labels.cancel || "Cancel"}</Button>
                <Button type="submit" disabled={busy} className="shadow-interactive" data-testid="inquiry-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={1.5} />}
                  {busy ? labels.sending : labels.submit}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
